import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Search as IconSearch } from '@material-ui/icons';

import { APP_ERROR } from '~/constants';
import useReducerAndSaga from '~/hooks/useReducerAndSaga';
import * as pdvActions from '~/store/modules/pdv/pdv-actions';
import * as clientActions from '~/store/modules/cliente/cliente-actions';
import saga from '~/store/modules/cliente/cliente-sagas';
import reducer from '~/store/modules/cliente/cliente-reducer';

import ConsultaClienteItem from '../ConsultaClienteItem';
import { Box, Paper, Typography } from '@material-ui/core';

const InputPesquisaCliente = ({ value, cliente, callback, setFocus }) => {
  const dispatch = useDispatch();
  const searchRef = useRef(null);
  const autoCompleteRef = useRef(null);

  const applicationMode = useSelector(state => state.app.applicationMode);
  const { vendedor: vendedorUsuarioLogado } = useSelector(state => state.auth);
  const { fatVendedor: vendedorSelecionadoVenda } = useSelector(state => state.pdv.venda);
  const { vendedores: listagemVendedores } = useSelector(state => state.pdv.selects ?? {});

  const [{ loading, items }, dispatchCliente] = useReducerAndSaga(reducer, saga, {
    loading: false,
    items: []
  });
  const [searchInput, setSearchInput] = useState(value ?? '');
  const [focused, setFocused] = useState(false);

  const changeSearchInput = queryText => {
    setSearchInput(queryText);
    if (!queryText.trim()) {
      dispatchCliente(clientActions.searchSuccess([]));
      return;
    }

    const vendedorId = vendedorSelecionadoVenda?.id ?? vendedorUsuarioLogado?.id ?? null
    const vendedor = listagemVendedores?.find(({ id }) => id === vendedorId) ?? null;

    try {
      dispatchCliente(clientActions.searchRequest(queryText, (vendedor ?? null)));
    } catch (error) {
      dispatch({ type: APP_ERROR, error });
    }
  };

  const handleChange = value => {
    autoCompleteRef.current = {
      value: null
    };
    setSearchInput('');
    searchRef.current = {
      value: ''
    };

    if (callback) {
      callback(value);
    } else {
      dispatch(
        pdvActions.setShowModalIdentificarCliente({
          show: true,
          clienteSelecionado: value,
          callback: () => {
            dispatch(pdvActions.setShowModalConsultaCliente({ show: false }));
          }
        })
      );
    }
  };

  useEffect(() => {
    dispatchCliente(clientActions.setApplicationMode(applicationMode));
  }, [applicationMode]);

  useEffect(() => {
    try {
      if (setFocus) searchRef?.current?.focus();
    } catch (err) {
      return;
    }
  }, [setFocus]);

  const searchState = searchInput.trim().length < 3 ? 'minLength' : loading ? 'loading' : items.length === 0 ? 'empty' : 'results';
  const popupMessage = {minLength: 'Digite no mínimo 3 caracteres.', loading: 'Carregando...', empty: 'Nenhum registro encontrado.'}[searchState];

  return (
    <>
      <Autocomplete
        id="autocomplete-cliente"
        ref={autoCompleteRef}
        open={focused}
        loading={loading}
        PaperComponent={(props) => (
          <Paper {...props}>
            {searchState === 'results'
              ? props.children :
              searchState === 'minLength' ?
              <></>
              : (
                <Box p={2}>
                  <Typography color="textSecondary">
                    {popupMessage}
                  </Typography>
                </Box>
              )}
          </Paper>
        )}
        freeSolo
        loadingText="Carregando..."
        noOptionsText="Nenhum registro encontrado"
        fullWidth
        options={[...items]}
        getOptionLabel={option =>
          `${option?.pjCnpj?.toString()} | ${option?.pfCpf?.toString()} | ${option?.codigoInterno?.toString()} | ${
            option?.razaoSocial
          } | ${option?.fantasia}`
        }
        inputValue={searchInput ?? ''}
        onInputChange={(_, value) =>{ 
          changeSearchInput(value)}}
        onChange={(event, value) => {
          if (value?.desativado) {
            autoCompleteRef.current = {
              value: null
            };
            setSearchInput('');
            searchRef.current = {
              value: ''
            };
            return;
          }
          handleChange(value);
        }}
        value={autoCompleteRef?.current?.value ?? null}
        renderOption={item => <ConsultaClienteItem data={item} />}
        renderInput={params => (
          <TextField
            {...params}
            size="medium"
            variant="outlined"
            placeholder="Pesquise por código, razão social, nome fantasia, cpf/cnpj ou grupo de cliente"
            inputRef={searchRef}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <IconSearch />
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {(searchState == 'loading') ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
      />
    </>
  );
};

export default InputPesquisaCliente;
